import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReportGenerationSurfaceTheme } from '../report-generation/types';

type RendererState = 'idle' | 'loading' | 'ready' | 'fallback' | 'error';
type ThreeModule = typeof import('three');

type SceneHandle = {
  dispose: () => void;
};

export type WorkspaceLoadingThreeRendererState = RendererState;

export type WorkspaceLoadingThreeArtifactProps = {
  active?: boolean;
  surfaceTheme?: ReportGenerationSurfaceTheme;
  className?: string;
  testId?: string;
  canvasTestId?: string;
  onRendererStateChange?: (state: WorkspaceLoadingThreeRendererState) => void;
};

export type WorkspaceLoadingBrandMomentProps = Omit<
  WorkspaceLoadingThreeArtifactProps,
  'className' | 'testId'
> & {
  className?: string;
  artifactClassName?: string;
  artifactTestId?: string;
  label?: string;
  labelClassName?: string;
  labelTestId?: string;
};

const joinClasses = (...values: Array<string | false | null | undefined>) =>
  values.filter(Boolean).join(' ');

const disposeScene = (scene: InstanceType<ThreeModule['Scene']>) => {
  scene.traverse((object) => {
    const renderable = object as {
      geometry?: { dispose: () => void };
      material?: { dispose: () => void } | Array<{ dispose: () => void }>;
    };
    renderable.geometry?.dispose();
    if (Array.isArray(renderable.material)) {
      renderable.material.forEach((material) => material.dispose());
    } else {
      renderable.material?.dispose();
    }
  });
};

const resolveReducedMotion = () =>
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const createLoadingArtifactScene = (input: {
  THREE: ThreeModule;
  mount: HTMLDivElement;
  surfaceTheme: ReportGenerationSurfaceTheme;
  reducedMotion: boolean;
  canvasTestId: string;
  setRendererState: (state: RendererState) => void;
}): SceneHandle => {
  const {
    THREE,
    mount,
    surfaceTheme,
    reducedMotion,
    canvasTestId,
    setRendererState,
  } = input;
  const width = Math.max(mount.clientWidth || 132, 96);
  const height = Math.max(mount.clientHeight || 132, 96);
  const isDark = surfaceTheme === 'dark';
  const renderer = new THREE.WebGLRenderer({
    antialias: true,
    alpha: true,
    preserveDrawingBuffer: true,
  });
  renderer.setClearColor(0x000000, 0);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.setSize(width, height, false);
  renderer.domElement.setAttribute('data-testid', canvasTestId);
  renderer.domElement.setAttribute('aria-hidden', 'true');
  renderer.domElement.className = 'block h-full w-full';
  mount.replaceChildren(renderer.domElement);

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(42, width / Math.max(height, 1), 0.1, 100);
  camera.position.set(0, 0, 7.8);

  const group = new THREE.Group();
  scene.add(group);
  scene.add(new THREE.AmbientLight(0xffffff, isDark ? 1.35 : 1.6));

  const keyLight = new THREE.PointLight(isDark ? 0x8bc0d2 : 0x2697a2, 18, 12);
  keyLight.position.set(2.8, 2.6, 4.4);
  scene.add(keyLight);

  const rimLight = new THREE.PointLight(0x28e2a7, isDark ? 10 : 7, 10);
  rimLight.position.set(-3.8, -2.4, 3.2);
  scene.add(rimLight);

  const ringMaterial = new THREE.MeshStandardMaterial({
    color: isDark ? 0x8bc0d2 : 0x237062,
    emissive: 0x0f766e,
    emissiveIntensity: isDark ? 0.32 : 0.18,
    metalness: 0.22,
    roughness: 0.35,
    transparent: true,
    opacity: isDark ? 0.46 : 0.38,
  });
  const rings = [
    new THREE.Mesh(new THREE.TorusGeometry(1.52, 0.025, 10, 96), ringMaterial.clone()),
    new THREE.Mesh(new THREE.TorusGeometry(2.02, 0.018, 10, 112), ringMaterial.clone()),
    new THREE.Mesh(new THREE.TorusGeometry(2.45, 0.014, 10, 128), ringMaterial.clone()),
  ];
  rings[0].rotation.x = Math.PI / 2.7;
  rings[1].rotation.y = Math.PI / 2.35;
  rings[2].rotation.x = Math.PI / 2;
  rings[2].rotation.z = Math.PI / 5.5;
  rings.forEach((ring) => group.add(ring));

  const orbitMaterial = new THREE.MeshStandardMaterial({
    color: 0x28e2a7,
    emissive: 0x28e2a7,
    emissiveIntensity: isDark ? 0.86 : 0.58,
    roughness: 0.22,
    metalness: 0.18,
  });
  const orbitDots = Array.from({ length: 6 }, (_, index) => {
    const dot = new THREE.Mesh(new THREE.SphereGeometry(index % 2 === 0 ? 0.08 : 0.06, 18, 12), orbitMaterial.clone());
    group.add(dot);
    return dot;
  });

  const beamMaterial = new THREE.LineBasicMaterial({
    color: isDark ? 0x8bc0d2 : 0x2697a2,
    transparent: true,
    opacity: isDark ? 0.32 : 0.22,
  });
  const beams = [
    new THREE.Line(
      new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(-1.8, -1.08, -0.5),
        new THREE.Vector3(1.8, 1.08, 0.5),
      ]),
      beamMaterial,
    ),
    new THREE.Line(
      new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(-1.72, 1.08, 0.35),
        new THREE.Vector3(1.72, -1.08, -0.35),
      ]),
      beamMaterial.clone(),
    ),
  ];
  beams.forEach((beam) => group.add(beam));

  let frame = 0;
  let disposed = false;
  const startedAt = window.performance.now();

  const render = () => {
    renderer.render(scene, camera);
  };

  const animate = () => {
    if (disposed) {
      return;
    }
    const elapsed = (window.performance.now() - startedAt) / 1000;
    if (!reducedMotion) {
      group.rotation.y = elapsed * 0.62;
      group.rotation.x = Math.sin(elapsed * 0.82) * 0.12;
      rings[0].rotation.z = elapsed * 1.2;
      rings[1].rotation.x = Math.PI / 2.35 + elapsed * 0.48;
      rings[2].rotation.y = elapsed * -0.72;
      orbitDots.forEach((dot, index) => {
        const phase = elapsed * (1.35 + index * 0.08) + index * ((Math.PI * 2) / orbitDots.length);
        const radius = index % 2 === 0 ? 2.02 : 1.52;
        dot.position.set(
          Math.cos(phase) * radius,
          Math.sin(phase * 0.72) * 0.66,
          Math.sin(phase) * radius * 0.38,
        );
      });
    }
    render();
    frame = window.requestAnimationFrame(animate);
  };

  const handleResize = () => {
    const nextWidth = Math.max(mount.clientWidth || width, 96);
    const nextHeight = Math.max(mount.clientHeight || height, 96);
    renderer.setSize(nextWidth, nextHeight, false);
    camera.aspect = nextWidth / Math.max(nextHeight, 1);
    camera.updateProjectionMatrix();
    render();
  };

  const resizeObserver =
    typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(handleResize)
      : null;
  resizeObserver?.observe(mount);
  animate();
  setRendererState('ready');

  return {
    dispose: () => {
      disposed = true;
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
      resizeObserver?.disconnect();
      disposeScene(scene);
      renderer.dispose();
      if (mount.contains(renderer.domElement)) {
        mount.removeChild(renderer.domElement);
      }
    },
  };
};

const IntellioLogoCore = ({ visible }: { visible: boolean }) => (
  <div
    className={joinClasses(
      'pointer-events-none absolute inset-0 flex items-center justify-center transition-opacity duration-300',
      visible ? 'opacity-100' : 'opacity-0',
    )}
    style={{ zIndex: 20 }}
    data-testid="report-generation-preview-loading-intellio-logo-core"
  >
    <div
      className="relative flex items-center justify-center rounded-full"
      style={{
        width: 78,
        height: 78,
        background: 'rgba(7, 17, 18, 0.18)',
        boxShadow: '0 0 32px rgba(40, 226, 167, 0.3)',
      }}
    >
      <IntellioLogoMark className="drop-shadow-[0_8px_24px_rgba(40,226,167,0.42)]" />
    </div>
  </div>
);

const IntellioLogoMark = ({ className = '' }: { className?: string }) => (
  <svg
    viewBox="0 0 200 200"
    className={className}
    fill="none"
    style={{ display: 'block', width: 68, height: 68 }}
    shapeRendering="geometricPrecision"
    data-testid="report-generation-preview-loading-intellio-logo"
  >
    <g transform="translate(100 100)">
      <ellipse rx="25" ry="92" fill="#2DD7AE" />
      <ellipse rx="25" ry="92" fill="#2C9BA5" transform="rotate(60)" />
      <ellipse rx="25" ry="92" fill="#12BBAE" transform="rotate(90)" />
      <ellipse rx="25" ry="92" fill="#FF5B5F" transform="rotate(120)" />
      <ellipse rx="25" ry="92" fill="#2C9BA5" transform="rotate(240)" />
      <ellipse rx="25" ry="92" fill="#FF2D45" transform="rotate(300)" />
      <g fill="#FFE0A5" stroke="#FFFFFF" strokeWidth="3.2" strokeLinejoin="round">
        <path d="M0-34 C24-16 33 9 18 31 C7 48-7 48-18 31 C-33 9-24-16 0-34Z" />
        <path d="M-44-3 C-19-20 18-20 44-3 C24 13-24 13-44-3Z" />
        <path d="M-18 31 C-4 19 4 19 18 31 C8 45-8 45-18 31Z" />
      </g>
      <circle r="20" fill="#FFE0A5" opacity="0.92" />
      <g
        fill="none"
        stroke="#FFFFFF"
        strokeWidth="5.8"
        strokeLinecap="round"
        strokeOpacity="0.98"
      >
        <ellipse rx="55" ry="17" />
        <ellipse rx="55" ry="17" transform="rotate(60)" />
        <ellipse rx="55" ry="17" transform="rotate(-60)" />
      </g>
      <circle r="4.6" fill="#FFFFFF" opacity="0.98" />
    </g>
  </svg>
);

export function WorkspaceLoadingThreeArtifact({
  active = true,
  surfaceTheme = 'light',
  className = '',
  testId = 'workspace-loading-three-artifact',
  canvasTestId = 'report-generation-preview-loading-artifact-canvas',
  onRendererStateChange,
}: WorkspaceLoadingThreeArtifactProps) {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const [rendererState, setRendererState] = useState<RendererState>('idle');
  const isDark = surfaceTheme === 'dark';
  const reducedMotion = useMemo(resolveReducedMotion, []);

  useEffect(() => {
    onRendererStateChange?.(rendererState);
  }, [onRendererStateChange, rendererState]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!active) {
      setRendererState('idle');
      return undefined;
    }
    if (!mount || typeof window === 'undefined') {
      setRendererState('fallback');
      return undefined;
    }

    let cancelled = false;
    let sceneHandle: SceneHandle | null = null;
    setRendererState('loading');

    void import('three')
      .then((THREE) => {
        if (cancelled || !mountRef.current) {
          return;
        }
        try {
          sceneHandle = createLoadingArtifactScene({
            THREE,
            mount: mountRef.current,
            surfaceTheme,
            reducedMotion,
            canvasTestId,
            setRendererState,
          });
        } catch {
          if (!cancelled) {
            setRendererState('fallback');
          }
        }
      })
      .catch(() => {
        if (!cancelled) {
          setRendererState('error');
        }
      });

    return () => {
      cancelled = true;
      sceneHandle?.dispose();
    };
  }, [active, canvasTestId, reducedMotion, surfaceTheme]);

  const fallbackVisible = active && rendererState !== 'ready';
  const logoCoreVisible = active && (rendererState === 'ready' || fallbackVisible);

  return (
    <div
      className={joinClasses(
        'pointer-events-none relative isolate h-28 w-28 overflow-hidden rounded-full',
        isDark ? 'bg-[#05090A]/60 ring-1 ring-[#1B4A4E]/70' : 'bg-[#E8F7F6]/70 ring-1 ring-[#CDEEE9]',
        className,
      )}
      aria-hidden="true"
      data-testid={testId}
      data-renderer-state={rendererState}
      data-workspace-loading-artifact="threejs"
      data-artifact-core="intellio-logo"
    >
      <IntellioLogoCore visible={logoCoreVisible} />
      {fallbackVisible ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <div
            className={joinClasses(
              'relative h-20 w-20 rounded-full border',
              isDark ? 'border-[#1B4A4E]' : 'border-[#CDEEE9]',
            )}
          >
            <div
              className={joinClasses(
                'absolute inset-1 rounded-full border-2 border-transparent animate-spin motion-reduce:animate-none',
                isDark ? 'border-t-[#8BC0D2] border-r-[#28E2A7]' : 'border-t-[#2697A2] border-r-[#28E2A7]',
              )}
            />
          </div>
        </div>
      ) : null}
      <div
        ref={mountRef}
        className={joinClasses(
          'absolute inset-0 h-full w-full transition-opacity duration-300',
          rendererState === 'ready' ? 'opacity-30' : 'opacity-0',
        )}
        style={{ zIndex: 0 }}
      />
    </div>
  );
}

export function WorkspaceLoadingBrandMoment({
  active = true,
  surfaceTheme = 'light',
  className = '',
  artifactClassName = '',
  artifactTestId = 'workspace-loading-three-artifact',
  canvasTestId,
  onRendererStateChange,
  label = 'Intellio',
  labelClassName = '',
  labelTestId = 'workspace-loading-brand-wordmark',
}: WorkspaceLoadingBrandMomentProps) {
  const isDark = surfaceTheme === 'dark';

  return (
    <div
      className={joinClasses(
        'pointer-events-none flex flex-col items-center gap-4 text-center',
        className,
      )}
      aria-hidden="true"
      data-testid="workspace-loading-brand-moment"
    >
      <WorkspaceLoadingThreeArtifact
        active={active}
        surfaceTheme={surfaceTheme}
        testId={artifactTestId}
        canvasTestId={canvasTestId}
        onRendererStateChange={onRendererStateChange}
        className={artifactClassName}
      />
      <span
        className={joinClasses(
          'font-sans text-[18px] font-bold leading-none tracking-normal',
          isDark ? 'text-[#E8EFEF]' : 'text-[#162033]',
          labelClassName,
        )}
        data-testid={labelTestId}
      >
        {label}
      </span>
    </div>
  );
}
